import { isEnumMember } from '../../../helpers/types'
import { type FilterDetail } from '../../../types/beFiltersApi'
import {
  DateMatch,
  type DateRangeDetails,
  type DateSpec,
  DatesType,
  DateTimeMatch,
  type SubscriptionSpec,
  type EmailSpec,
  type SmsSpec,
  type DateTimeSpec,
  type DeviceSpec,
  EngagementMatch,
  type EngagementSpec,
  type FilterCondition,
  GeoMatch,
  type GeoSpec,
  GroupMatch,
  type GroupSpec,
  NumericMatch,
  type NumericSpec,
  SpecType,
  type SysSpec,
  TextMatch,
  type TextSpec,
  TrackingMatch,
  type TrackingSpec,
} from '../../../types/FilterCondition'
import {
  UiConditionType,
  type UiDeviceSpec,
  type UiEngagementSpec,
  type UiFilterCondition,
  type UiFilterDetail,
  type UiGenericSpec,
  type UiGeoSpec,
  type UiGroupSpec,
  type UiProfileSpec,
  type UiTrackingSpec,
  UiTextFormat,
} from '../filterConditionTypes'
import {
  fromEmailSpec,
  fromSmsSpec,
  fromSubscriptionSpec,
  requestDateMap,
  responseDateMap,
  toEmailSpec,
  toSmsSpec,
  toSubscriptionSpec,
} from './apimapping/activityApiMapping'
import { DATEMATCHGROUPS } from './conditionOptions'
import { getCondTypeBasedOnSpec } from './conditionUtils'
import {
  isUiDateSpec,
  isUiDateTimeSpec,
  isUiNumericSpec,
  isUiTextSpec,
  textMatchHasMultipleValues,
} from './profileFieldSpecValidation'

export const fromFilterDetail = (filter: FilterDetail): UiFilterDetail => {
  return {
    ...filter,
    Blocks: filter.Blocks.map((block) => {
      return {
        ...block,
        Conditions:
          block.Conditions.map<UiFilterCondition>(fromFilterCondition),
      }
    }),
  }
}

export const toFilterDetail = (f: UiFilterDetail): FilterDetail => {
  const { IsUsedInOtherLists: _, ...filter } = f
  return {
    ...filter,
    Blocks: filter.Blocks.map((block) => {
      return {
        ...block,
        Conditions: block.Conditions.map<FilterCondition>((c) =>
          toFilterCondition(c, f),
        ),
      }
    }),
  }
}

const toFilterCondition = (
  c: UiFilterCondition,
  f: UiFilterDetail,
): FilterCondition => {
  const mappedCond = toFilterConditionTypes(c, f)
  if (mappedCond.SpecType === undefined || mappedCond.Spec === undefined) {
    throw new Error('Failed to map condition')
  }
  return {
    JoinType: c.JoinType,
    SpecType: mappedCond.SpecType,
    Spec: mappedCond.Spec,
  }
}

const toFilterConditionTypes = (
  ui: UiFilterCondition,
  f: UiFilterDetail,
): Partial<FilterCondition> => {
  switch (ui.ConditionType) {
    case UiConditionType.GroupBelongs:
      return toGroupSpec(ui, true)
    case UiConditionType.GroupNotBelongs:
      return toGroupSpec(ui, false)
    case UiConditionType.ProfileField:
      return toProfileFieldSpec(ui)

    case UiConditionType.EmailSubscribed:
    case UiConditionType.EmailUnsubscribed:
      return toSubscriptionSpec(ui)
    case UiConditionType.EmailSent:
    case UiConditionType.EmailSuccessfullySent:
    case UiConditionType.EmailNotSent:
    case UiConditionType.EmailOpened:
    case UiConditionType.EmailNotOpened:
    case UiConditionType.EmailBouncedHB:
    case UiConditionType.EmailBouncedSB:
    case UiConditionType.EmailNotBouncedHB:
    case UiConditionType.EmailPerformedAtLeast1Action:
    case UiConditionType.EmailPerformedLessThan5Actions:
    case UiConditionType.EmailPerformedLessThan10Actions:
    case UiConditionType.EmailPerformed10OrMoreActions:
    case UiConditionType.EmailClicked:
    case UiConditionType.EmailNotClicked:
      return toEmailSpec(ui, f)
    case UiConditionType.SmsSent:
    case UiConditionType.SmsNotSent:
    case UiConditionType.SmsSuccessfullySent:
    case UiConditionType.SmsNotReceived:
    case UiConditionType.SmsExpired:
    case UiConditionType.SmsRejected:
      return toSmsSpec(ui, f)

    case UiConditionType.Engagement:
      return toEngagementSpec(ui)
    case UiConditionType.GeoLocatedIn:
    case UiConditionType.GeoNotLocatedIn:
      return toGeoSpec(ui)
    case UiConditionType.Device:
      return toDeviceSpec(ui)
    case UiConditionType.TrackingActive:
    case UiConditionType.TrackingAnonymous:
      return toTrackingSpec(ui)
    default:
      throw new Error(`ConditionType ${ui.ConditionType} not handled`)
  }
}

const toTrackingSpec = (ui: UiFilterCondition): Partial<FilterCondition> => {
  const uiSpec = ui.Spec as UiTrackingSpec
  const match = uiSpec.TrackingMatchOperator

  if (match === undefined) {
    throw new Error('TrackingMatchOperator is required')
  }

  const details = uiSpec.Dates?.Details
  const dates =
    match === TrackingMatch.TrackingAnytime
      ? {
          Type: DatesType.DateRangeDetails,
          Details: { From: undefined, To: undefined },
        }
      : details?.From instanceof Date && details.To instanceof Date
        ? {
            Type: DatesType.DateRangeDetails,
            Details: {
              From: requestDateMap(details.From, false),
              To: requestDateMap(details.To, true),
            },
          }
        : undefined

  if (dates === undefined) {
    throw new Error('Tracking date range requires From and To')
  }

  const spec: TrackingSpec = {
    Identifier: uiSpec.Identifier,
    TrackingActive: uiSpec.TrackingActive,
    TrackingMatchOperator: match,
    Dates: dates,
  }

  return {
    SpecType: SpecType.TrackingSpec,
    Spec: spec,
  }
}

const toDeviceSpecType = (
  from: UiDeviceSpec,
): SpecType.SysSpec | SpecType.DeviceSpec => {
  switch (from.Value) {
    case 'DeviceSpec_Android':
    case 'DeviceSpec_BlackBerry':
    case 'DeviceSpec_PC_Apple':
    case 'DeviceSpec_PC_Linux':
    case 'DeviceSpec_PC_Windows':
    case 'DeviceSpec_Symbian':
    case 'DeviceSpec_Windows_Phone':
    case 'DeviceSpec_iPad':
    case 'DeviceSpec_iPhone':
      return SpecType.DeviceSpec

    case 'SysSpec_Android':
    case 'SysSpec_Apple_Mac_OS':
    case 'SysSpec_Apple_iOS':
    case 'SysSpec_BlackBerry':
    case 'SysSpec_Linux':
    case 'SysSpec_MS_Windows':
    case 'SysSpec_MS_Windows_Phone':
    case 'SysSpec_Symbian':
      return SpecType.SysSpec
  }

  throw Error(`Unsupported UiDeviceSpe Value: ${from.Value}`)
}

const toDeviceSpecCode = (from: UiDeviceSpec): number => {
  switch (from.Value) {
    case 'DeviceSpec_iPad':
      return 1
    case 'DeviceSpec_iPhone':
      return 2
    case 'DeviceSpec_BlackBerry':
      return 3
    case 'DeviceSpec_Windows_Phone':
      return 4
    case 'DeviceSpec_Symbian':
      return 5
    case 'DeviceSpec_Android':
      return 6
    case 'DeviceSpec_PC_Windows':
      return 7
    case 'DeviceSpec_PC_Apple':
      return 8
    case 'DeviceSpec_PC_Linux':
      return 9
    //
    case 'SysSpec_MS_Windows':
      return 1
    case 'SysSpec_MS_Windows_Phone':
      return 2
    case 'SysSpec_Apple_Mac_OS':
      return 3
    case 'SysSpec_Apple_iOS':
      return 4
    case 'SysSpec_BlackBerry':
      return 5
    case 'SysSpec_Android':
      return 6
    case 'SysSpec_Linux':
      return 7
    case 'SysSpec_Symbian':
      return 8
  }

  throw Error(`Unsupported UiDeviceSpec Value: ${from.Value}`)
}

const toDeviceSpec = (ui: UiFilterCondition): Partial<FilterCondition> => {
  const spec = ui.Spec as UiDeviceSpec

  return {
    SpecType: toDeviceSpecType(spec),
    Spec: {
      Precision: 3,
      Code: toDeviceSpecCode(spec),
    },
  }
}

const toGeoSpec = (ui: UiFilterCondition): Partial<FilterCondition> => {
  const from = ui.Spec as UiGeoSpec
  const spec: GeoSpec = {
    GeoMatchOperator:
      from.GeoMatchOperator === 'LocatedIn'
        ? GeoMatch.LocatedIn
        : GeoMatch.NotLocatedIn,
    Precision: from.Precision,
    Country: from.Country,
    Region: from.Region,
  }

  return {
    SpecType: SpecType.GeoSpec,
    Spec: spec,
  }
}

const toEngagementSpec = (ui: UiFilterCondition): Partial<FilterCondition> => {
  const spec: EngagementSpec = {
    EngagementMatchOperator: EngagementMatch.EqualTo,
    Values: [toEngagementSpecValue(ui.Spec as UiEngagementSpec)],
  }
  return {
    SpecType: SpecType.EngagementSpec,
    Spec: spec,
  }
}

const toGroupSpec = (
  ui: UiFilterCondition,
  belongs: boolean,
): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.GroupSpec,
  }
  const spec: GroupSpec = {
    GroupMatchOperator: belongs
      ? GroupMatch.BelongsTo
      : GroupMatch.NotBelongsTo,
    Values: (ui.Spec as UiGroupSpec).Values,
  }
  cond.Spec = spec
  return cond
}

const toProfileFieldSpec = (
  ui: UiFilterCondition,
): Partial<FilterCondition> => {
  const uiSpec = ui.Spec as UiProfileSpec
  if (isEnumMember<TextMatch>(uiSpec.Match, TextMatch)) {
    return toTextSpec(uiSpec)
  }
  if (isEnumMember<NumericMatch>(uiSpec.Match, NumericMatch)) {
    return toNumericSpec(uiSpec)
  }
  if (isEnumMember<DateMatch>(uiSpec.Match, DateMatch)) {
    return toDateSpec(uiSpec)
  }
  if (isEnumMember<DateTimeMatch>(uiSpec.Match, DateTimeMatch)) {
    return toDateTimeSpec(uiSpec)
  }
  throw new Error(`ProfileField Match not handled`)
}

const toTextSpec = (uiSpec: UiProfileSpec): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.TextSpec,
  }
  if (!isUiTextSpec(uiSpec)) {
    throw new Error('Incorrect UiTextSpec')
  }
  const spec: TextSpec = {
    Identifier: uiSpec.Identifier,
    TextMatchOperator: uiSpec.Match,
    Values: textMatchHasMultipleValues(uiSpec.Match)
      ? (uiSpec.Value?.split(',') ?? [])
      : uiSpec.Value !== undefined
        ? [uiSpec.Value]
        : [],
  }
  cond.Spec = spec
  return cond
}

const toNumericSpec = (uiSpec: UiProfileSpec): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.NumericSpec,
  }
  if (!isUiNumericSpec(uiSpec)) {
    throw new Error('Incorrect UiNumericSpec')
  }
  const spec: NumericSpec = {
    Identifier: uiSpec.Identifier,
    Format: uiSpec.Format,
    NumericMatchOperator: uiSpec.Match,
    Values: [uiSpec.Value],
  }
  cond.Spec = spec
  return cond
}

const toDateSpec = (uiSpec: UiProfileSpec): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.DateSpec,
  }
  if (!isUiDateSpec(uiSpec)) {
    throw new Error('Incorrect UiDateSpec')
  }
  const spec: DateSpec = {
    Identifier: uiSpec.Identifier,
    Format: uiSpec.Format,
    DateMatchOperator: uiSpec.Match,
    Values: uiSpec.Value === undefined ? [] : [`${uiSpec.Value}`],
  }
  cond.Spec = spec
  return cond
}

const toDateTimeSpec = (uiSpec: UiProfileSpec): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.DateTimeSpec,
  }
  if (!isUiDateTimeSpec(uiSpec)) {
    throw new Error('Incorrect UiDateTimeSpec')
  }
  const spec: DateTimeSpec = {
    Identifier: uiSpec.Identifier,
    Format: uiSpec.Format,
    DateTimeMatchOperator: uiSpec.Match,
    Values: [`${uiSpec.Value}`],
  }
  cond.Spec = spec
  return cond
}

const fromFilterCondition = (c: FilterCondition): UiFilterCondition => {
  let spec: UiGenericSpec
  if (isTrackingSpec(c.Spec)) {
    spec = fromTrackingSpec(c.Spec)
  } else {
    switch (c.SpecType) {
      case SpecType.GroupSpec:
        spec = fromGroupSpec(c.Spec as GroupSpec)
        break
      case SpecType.TextSpec:
        spec = fromTextSpec(c.Spec as TextSpec)
        break
      case SpecType.NumericSpec:
        spec = fromNumericSpec(c.Spec as NumericSpec)
        break
      case SpecType.DateSpec:
        spec = fromDateSpec(c.Spec as DateSpec)
        break
      case SpecType.DateTimeSpec:
        spec = fromDateTimeSpec(c.Spec as DateTimeSpec)
        break

      case SpecType.SubscriptionSpec:
        spec = fromSubscriptionSpec(c.Spec as SubscriptionSpec)
        break
      case SpecType.EmailSpec:
        spec = fromEmailSpec(c.Spec as EmailSpec)
        break
      case SpecType.SmsSpec:
        spec = fromSmsSpec(c.Spec as SmsSpec)
        break

      case SpecType.EngagementSpec:
        spec = fromEngagementSpec(c.Spec as EngagementSpec)
        break
      case SpecType.GeoSpec:
        spec = fromGeoSpec(c.Spec as GeoSpec)
        break
      case SpecType.SysSpec:
        spec = fromSysSpec(c.Spec as SysSpec)
        break
      case SpecType.DeviceSpec:
        spec = fromDeviceSpec(c.Spec as DeviceSpec)
        break
      case SpecType.TrackingSpec:
        if (!isTrackingSpec(c.Spec)) {
          throw new Error('Invalid TrackingSpec payload')
        }
        spec = fromTrackingSpec(c.Spec)
        break
      default:
        throw new Error(`SpecType ${c.SpecType} not handled`)
    }
  }
  return {
    JoinType: c.JoinType,
    ConditionType: getCondTypeBasedOnSpec(c),
    Spec: spec,
  }
}

const isTrackingSpec = (
  spec: FilterCondition['Spec'],
): spec is TrackingSpec => {
  return (
    typeof spec === 'object' &&
    spec !== null &&
    'TrackingActive' in spec &&
    typeof spec.TrackingActive === 'boolean'
  )
}

const fromTrackingSpec = (spec: TrackingSpec): UiTrackingSpec => {
  const details = spec.Dates?.Details as DateRangeDetails | undefined
  const from = responseDateMap(details?.From, false)
  const to = responseDateMap(details?.To, true)
  const trackingMatchOperator =
    spec.TrackingMatchOperator ??
    (from !== null && to !== null
      ? TrackingMatch.TrackingDate
      : TrackingMatch.TrackingAnytime)

  return {
    Identifier: spec.Identifier,
    TrackingActive: spec.TrackingActive,
    TrackingMatchOperator: trackingMatchOperator,
    Dates: {
      Type: 'DateRangeDetails',
      Details: {
        From: from,
        To: to,
      },
    },
  }
}

const fromGroupSpec = (spec: GroupSpec): UiGroupSpec => {
  return {
    Values: spec.Values,
  }
}

const fromTextSpec = (spec: TextSpec): UiProfileSpec => {
  return {
    Identifier: spec.Identifier,
    Format: UiTextFormat.Text,
    Match: spec.TextMatchOperator,
    Value: spec.Values.join(','),
  }
}

const fromNumericSpec = (spec: NumericSpec): UiProfileSpec => {
  return {
    Identifier: spec.Identifier,
    Format: spec.Format,
    Match: spec.NumericMatchOperator,
    Value: spec.Values[0],
  }
}

const fromDateSpec = (spec: DateSpec): UiProfileSpec => {
  return {
    Identifier: spec.Identifier,
    Format: spec.Format,
    Match: spec.DateMatchOperator,
    Value: DATEMATCHGROUPS.FIXED.includes(spec.DateMatchOperator)
      ? spec.Values[0]
      : parseInt(spec.Values[0]),
  }
}

const fromDateTimeSpec = (spec: DateTimeSpec): UiProfileSpec => {
  return {
    Identifier: spec.Identifier,
    Format: spec.Format,
    Match: spec.DateTimeMatchOperator,
    Value: parseInt(spec.Values[0]),
  }
}

const fromSysSpec = (spec: SysSpec): UiDeviceSpec => {
  switch (spec.Code) {
    case 1:
      return { Value: 'SysSpec_MS_Windows' }
    case 2:
      return { Value: 'SysSpec_MS_Windows_Phone' }
    case 3:
      return { Value: 'SysSpec_Apple_Mac_OS' }
    case 4:
      return { Value: 'SysSpec_Apple_iOS' }
    case 5:
      return { Value: 'SysSpec_BlackBerry' }
    case 6:
      return { Value: 'SysSpec_Android' }
    case 7:
      return { Value: 'SysSpec_Linux' }
    case 8:
      return { Value: 'SysSpec_Symbian' }
  }
  throw Error(`Unsupported SysSpec Code: ${spec.Code}`)
}

const fromDeviceSpec = (spec: DeviceSpec): UiDeviceSpec => {
  switch (spec.Code) {
    case 1:
      return { Value: 'DeviceSpec_iPad' }
    case 2:
      return { Value: 'DeviceSpec_iPhone' }
    case 3:
      return { Value: 'DeviceSpec_BlackBerry' }
    case 4:
      return { Value: 'DeviceSpec_Windows_Phone' }
    case 5:
      return { Value: 'DeviceSpec_Symbian' }
    case 6:
      return { Value: 'DeviceSpec_Android' }
    case 7:
      return { Value: 'DeviceSpec_PC_Windows' }
    case 8:
      return { Value: 'DeviceSpec_PC_Apple' }
    case 9:
      return { Value: 'DeviceSpec_PC_Linux' }
  }
  throw Error(`Unsupported DeviceSpec Code: ${spec.Code}`)
}

const fromGeoSpec = (spec: GeoSpec): UiGeoSpec => {
  return {
    GeoMatchOperator: spec.GeoMatchOperator,
    Precision: spec.Precision,
    Country: spec.Country,
    Region: spec.Region,
  }
}

const fromEngagementSpec = (spec: EngagementSpec): UiEngagementSpec => {
  return {
    Value: fromEngagementSpecValue(spec.Values),
  }
}

const fromEngagementSpecValue = (
  v: EngagementSpec['Values'],
): UiEngagementSpec['Value'] => {
  switch (v[0]) {
    case 0:
      return 'Bounce'
    case 1:
      return 'New'
    case 2:
      return 'Inactive'
    case 3:
      return 'LessActive'
    case 4:
      return 'Active'
    case 5:
      return 'VeryActive'
    case 6:
      return 'Abandoned'
  }
}

const toEngagementSpecValue = (
  ui: UiEngagementSpec,
): EngagementSpec['Values'][0] => {
  switch (ui.Value) {
    case 'Bounce':
      return 0
    case 'New':
      return 1
    case 'Inactive':
      return 2
    case 'LessActive':
      return 3
    case 'Active':
      return 4
    case 'VeryActive':
      return 5
    case 'Abandoned':
      return 6
  }

  return 0
}
